import React, { FC, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useScreenDetector } from '../../../hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleRight,
  faAngleLeft,
  faAngleDown,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '../../Link/Link';
import styles from './PlayerPortalsBurgerMenu.module.scss';

import {
  AUTH_CONTEXT_KEYS,
  getRolesFromItem,
  useAuthContext,
} from '@r-and-a-shared-ui/auth-client';
import { PlayerPortalsIDCardProps } from '../PlayerPortalsIDCard/PlayerPortalsIDCard';
import { getAllUserRolesFromChampionship } from '@r-and-a-shared-ui/common-ui';
import { usePlayerPortalsConfigContext } from '@r-and-a-shared-ui/common-ui';

export interface PlayerPortalsBurgerMenuProps {
  menuItems: BurgerMenuItem[];
  isOpenDefault?: boolean;
  idCard?: PlayerPortalsIDCardProps;
}

export type BurgerMenuItem =
  | {
      title: string;
      href: string;
      codename?: string;
      internal: boolean;
      children?: never;
      onClick?: () => void;
      roles?: Array<{ name: string; codename: string }>;
    }
  | {
      title: string;
      children: BurgerMenuItem[];
      roles?: Array<{ name: string; codename: string }>;
    };

export type BurgerMenuItemProps = {
  onClick: (item: BurgerMenuItem) => void;
  item: BurgerMenuItem;
  level?: number;
  resetKey?: number;
};

export const PlayerPortalsBurgerMenuItem: FC<BurgerMenuItemProps> = ({
  item,
  onClick,
  level = 0,
  resetKey = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isBranch = !!item.children;
  const { isMobile } = useScreenDetector();

  const { userState } = useAuthContext(AUTH_CONTEXT_KEYS.PP_CONTEXT_KEY);
  const { currentChampionship } = usePlayerPortalsConfigContext() || {};

  const userRoles: string[] | null = getAllUserRolesFromChampionship(
    userState,
    currentChampionship,
  );
  const itemsRoles: string[] | null = getRolesFromItem(item, true);

  const shouldHide = getShouldHideMenuItem(userRoles, itemsRoles);

  function getShouldHideMenuItem(
    userRoles: string[] | null,
    itemsRoles: string[] | null,
  ) {
    if (userRoles === null || itemsRoles === null) {
      return true;
    }
    return (
      itemsRoles.length > 0 &&
      (!userState.user ||
        !userRoles.length ||
        !itemsRoles.some(r => userRoles.includes(r.toLowerCase())))
    );
  }
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isBranch) setIsOpen(o => !o);
  };

  const closeThisBranch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  useEffect(() => {
    setIsOpen(false);
  }, [resetKey]);

  return shouldHide ? null : (
    <li onClick={() => !isBranch && onClick(item)} role="presentation">
      {isBranch ? (
        <button
          onClick={toggle}
          className={isOpen ? styles['is-active'] : undefined}
          aria-expanded={isOpen}
          aria-haspopup="true"
          role="menuitem"
        >
          {item.title}
          {isOpen ? (
            <FontAwesomeIcon icon={faAngleDown} />
          ) : (
            <FontAwesomeIcon icon={faAngleRight} />
          )}
        </button>
      ) : (
        <Link
          title={item.title}
          href={item.href}
          internal={item.internal}
          codename={item.codename}
          role="menuitem"
        />
      )}
      {isBranch && (
        <ul
          className={classnames(
            styles['submenu-list'],
            styles[`level-${level + 1}`],
            !isOpen && styles['is-hidden'],
          )}
          data-level={level + 1}
          role="menu"
          aria-hidden={!isOpen}
        >
          {isMobile && (
            <li className={styles['submenu-back']}>
              <button onClick={closeThisBranch} className={styles['back-btn']}>
                <FontAwesomeIcon icon={faAngleLeft} />
                Back
              </button>
            </li>
          )}
          {item.children.map((childItem, index) => (
            <PlayerPortalsBurgerMenuItem
              key={index}
              item={childItem}
              onClick={onClick}
              level={level + 1}
              resetKey={resetKey}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
